<script setup lang="ts">
import { computed } from 'vue'
import { Download, CircleCheckFilled, WarningFilled, Close } from '@element-plus/icons-vue'
import { useDownloadTasks, removeTask } from '@/shared/composables/useFileDownload'

const { tasks } = useDownloadTasks()

const visible = computed(() => tasks.value.length > 0)

function formatSize(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)}KB`
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)}MB`
  return `${(bytes / 1073741824).toFixed(2)}GB`
}

/** 进度文案：下载中显示百分比 / 已完成 / 失败原因 */
function statusText(progress: number, status: string, error?: string): string {
  if (status === 'done') return '已完成'
  if (status === 'error') return error || '下载失败'
  if (progress < 0) return '下载中…'
  return `${progress}%`
}
</script>

<template>
  <Transition name="dl-slide">
    <div v-if="visible" class="download-panel">
      <div class="download-panel-header">
        <el-icon :size="14"><Download /></el-icon>
        <span>下载任务</span>
        <span class="download-count">{{ tasks.length }}</span>
      </div>

      <div class="download-list">
        <div v-for="task in tasks" :key="task.id" class="download-item">
          <div class="download-item-top">
            <span class="download-name" :title="task.name">{{ task.name }}</span>
            <el-icon v-if="task.status === 'done'" :size="14" class="ic-done"><CircleCheckFilled /></el-icon>
            <el-icon v-else-if="task.status === 'error'" :size="14" class="ic-error"><WarningFilled /></el-icon>
            <button v-else class="download-close" @click="removeTask(task.id)">
              <el-icon :size="12"><Close /></el-icon>
            </button>
          </div>

          <div class="download-item-body">
            <el-progress
              v-if="task.status !== 'error' && task.progress >= 0"
              :percentage="task.progress"
              :status="task.status === 'done' ? 'success' : undefined"
              :stroke-width="5"
              :show-text="false"
            />
            <div v-else-if="task.status === 'downloading'" class="download-bar-indeterminate" />
            <div class="download-meta">
              <span class="download-status" :class="task.status">{{ statusText(task.progress, task.status, task.error) }}</span>
              <span v-if="task.total" class="download-size">{{ formatSize(task.loaded) }} / {{ formatSize(task.total) }}</span>
            </div>
          </div>

          <button v-if="task.status === 'error'" class="download-dismiss" @click="removeTask(task.id)">关闭</button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.download-panel {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 320px;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
  background: var(--el-bg-color-overlay, #fff);
  border: 1px solid var(--el-border-color-light, #ebeef5);
  border-radius: 10px;
  box-shadow: var(--el-box-shadow-light, 0 2px 12px rgba(0, 0, 0, 0.1));
  z-index: 1500;
  overflow: hidden;
}
.download-panel-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
  border-bottom: 1px solid var(--el-border-color-lighter, #f2f6fc);
}
.download-count {
  margin-left: auto;
  font-size: 12px;
  font-weight: 400;
  color: var(--el-text-color-secondary, #909399);
}
.download-list {
  padding: 4px 0;
  overflow-y: auto;
}
.download-item {
  padding: 8px 12px;
  border-bottom: 1px solid var(--el-border-color-lighter, #f2f6fc);
}
.download-item:last-child { border-bottom: none; }
.download-item-top {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.download-name {
  flex: 1;
  font-size: 12px;
  color: var(--el-text-color-primary, #303133);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ic-done { color: var(--el-color-success, #67c23a); }
.ic-error { color: var(--el-color-danger, #f56c6c); }
.download-close {
  display: inline-flex;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--el-text-color-secondary, #909399);
  padding: 0;
}
.download-item-body { padding-left: 2px; }
.download-meta {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 11px;
  color: var(--el-text-color-secondary, #909399);
}
.download-status.error { color: var(--el-color-danger, #f56c6c); }
.download-status.done { color: var(--el-color-success, #67c23a); }
.download-dismiss {
  margin-top: 6px;
  font-size: 12px;
  color: var(--el-color-primary, #409eff);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
/* 总大小未知时的滚动条 */
.download-bar-indeterminate {
  height: 5px;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--el-color-primary-light-5, #a0cfff) 0%, var(--el-color-primary, #409eff) 50%, var(--el-color-primary-light-5, #a0cfff) 100%);
  background-size: 200% 100%;
  animation: dl-indeterminate 1.2s linear infinite;
}
@keyframes dl-indeterminate {
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
}
.dl-slide-enter-active, .dl-slide-leave-active { transition: all 0.25s ease; }
.dl-slide-enter-from, .dl-slide-leave-to { opacity: 0; transform: translateY(12px); }
</style>
